import React, {
  useState,
  useEffect,
  useMemo,
} from 'react';
import {
  Navigate,
  matchPath,
  useLocation,
  useSearchParams,
  useNavigationType,
} from 'react-router-dom';
import { LocalizationContextProvider as UiKitLocalizationContextProvider } from '@/ui-kit/localization';
import { ThemeContextProvider as UiKitThemeContextProvider } from '@/ui-kit/theme';
import { Toast } from '@/ui-kit/Toast';
import { Loader } from '@/ui-kit/loader';
import {
  IAuthData,
  AuthContextProvider,
  useGetClientAuthDataByAuthCode,
} from '@/features/auth';
import { useGetPublicWebsite } from '@/features/website';
import { ThemeContextProvider } from '@/features/theme';
import { LanguageContextProvider } from '@/features/language';
import { TimezoneContextProvider } from '@/features/timezone';
import { EmbedContextProvider } from '@/features/embed';
import { store } from '@/services/store';
import { analytics } from '@/services/analytics';
import { Router } from '@/router';
import { ROUTES } from '@/constants/routes';
import { WebsiteAnalytics } from './website-analytics';
import { IframeResizer } from './iframe-resizer';
import { useLocalizedTexts } from './localization';

export const App = () => {
  const l = useLocalizedTexts();
  const [authData, setAuthData] = useState<IAuthData>();
  const location = useLocation();
  const navType = useNavigationType();
  const [query] = useSearchParams();
  const authCode = query.get('authCode') ?? undefined;
  const isEmbedded = query.get('embedded') === 'true';
  const embeddedId = query.get('embeddedId') ?? undefined;
  const embeddedUrl = isEmbedded ? location.pathname : undefined;
  const [embedContextValue] = useState(() => {
    return {
      isEmbedded,
      embeddedId,
      embeddedUrl,
    };
  });
  const pathnameArray = location.pathname.split('/');
  const userId = pathnameArray[1] === 'users' ? pathnameArray[2] : undefined;
  // const domain = pathnameArray[1] !== 'users' ? pathnameArray[1] : undefined;
  const isNotFoundCurrentRoute = !!matchPath(ROUTES.NOT_FOUND, location.pathname);
  const clientAuthDataByAuthCodeMutation = useGetClientAuthDataByAuthCode();

  useEffect(() => {
    if (navType !== 'POP') {
      window.scrollTo({
        top: 0,
        behavior: 'auto',
      });
    }
  }, [location]);

  useEffect(() => {
    if (userId) {
      analytics.init({ userId });
    }
    return () => {
      analytics.destroy();
    };
  }, [userId]);

  useEffect(() => {
    const init = async () => {
      if (authCode) {
        const data = await clientAuthDataByAuthCodeMutation.mutateAsync({ authCode });
        setAuthData(data);
      }
    };

    init();
  }, [authCode]);

  const publicWebsiteQuery = useGetPublicWebsite({ userId });

  useEffect(() => {
    if (publicWebsiteQuery.data?.domain) {
      document.title = `Workee${publicWebsiteQuery.data.domain ? ' | ' : ''}${publicWebsiteQuery.data.domain ?? ''}`;
    }
  }, [publicWebsiteQuery]);

  const uiKitLocalizationContextValue = useMemo(() => {
    return {
      datePickerGridHeaderMonText: l.datePickerGridHeaderMonText,
      datePickerGridHeaderTueText: l.datePickerGridHeaderTueText,
      datePickerGridHeaderWedText: l.datePickerGridHeaderWedText,
      datePickerGridHeaderThuText: l.datePickerGridHeaderThuText,
      datePickerGridHeaderFriText: l.datePickerGridHeaderFriText,
      datePickerGridHeaderSatText: l.datePickerGridHeaderSatText,
      datePickerGridHeaderSunText: l.datePickerGridHeaderSunText,
      phoneInputPlaceholder: l.phoneInputPlaceholder,
    };
  }, [
    l.datePickerGridHeaderMonText,
    l.datePickerGridHeaderTueText,
    l.datePickerGridHeaderWedText,
    l.datePickerGridHeaderThuText,
    l.datePickerGridHeaderFriText,
    l.datePickerGridHeaderSatText,
    l.datePickerGridHeaderSunText,
    l.phoneInputPlaceholder,
  ]);

  const uiKitThemeContextValue = useMemo(() => {
    if (!publicWebsiteQuery.data?.configuration?.theme.colors.primary) {
      return undefined;
    }

    return { primaryColor: publicWebsiteQuery.data.configuration.theme.colors.primary };
  }, [publicWebsiteQuery.data?.configuration?.theme.colors.primary]);

  const themeContextValue = useMemo(() => {
    if (!publicWebsiteQuery.data?.configuration?.theme.colors.primary) {
      return undefined;
    }

    return { primaryColor: publicWebsiteQuery.data.configuration.theme.colors.primary };
  }, [publicWebsiteQuery.data?.configuration?.theme.colors.primary]);

  if (!isNotFoundCurrentRoute && userId && publicWebsiteQuery.isLoading) {
    return <Loader fullScreen />;
  }

  if (!isNotFoundCurrentRoute && (userId && !publicWebsiteQuery.data)) {
    store.clear();
    return <Navigate to={ROUTES.NOT_FOUND} replace />;
  }

  return (
    <>
      <AuthContextProvider
        value={authData}
        isLoading={clientAuthDataByAuthCodeMutation.isLoading}
      >
        <UiKitLocalizationContextProvider value={uiKitLocalizationContextValue}>
          <UiKitThemeContextProvider value={uiKitThemeContextValue}>
            <EmbedContextProvider value={embedContextValue}>
              <ThemeContextProvider value={themeContextValue}>
                <LanguageContextProvider>
                  <TimezoneContextProvider>
                    <div className="App">
                      <Toast />
                      <Router />
                    </div>
                  </TimezoneContextProvider>
                </LanguageContextProvider>
              </ThemeContextProvider>
            </EmbedContextProvider>
          </UiKitThemeContextProvider>
        </UiKitLocalizationContextProvider>
      </AuthContextProvider>
      <WebsiteAnalytics analyticCredentials={publicWebsiteQuery.data?.analyticCredentials} />
      {embedContextValue.isEmbedded ? (
        <IframeResizer embeddedId={embedContextValue.embeddedId} />
      ) : null}
    </>
  );
};
